import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { Loader2, Truck, FlaskConical, ArrowUpRight, Download, Trash2 } from "lucide-react";
import { AppShell } from "@/components/Layout";
import { CTAButton, KPI, MetricRow, Overline, ScoreRing, StatusPill, Timeline } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { t, ts, tb, tm, tn, pick } = useI18n();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const emptyAccount = useCallback(() => ({
    user,
    active_sample: null,
    samples: [],
    notifications: [],
    questionnaire: null,
    orders: [],
    evolution: { score_delta: null, points: [] },
  }), [user]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/me/overview");
      if (!res.data || typeof res.data !== "object" || !Array.isArray(res.data.samples)) {
        setData(emptyAccount());
        return;
      }
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 405) {
        setData(emptyAccount());
      } else {
        toast.error(apiError(e.response?.data?.detail, e.message));
      }
    }
  }, [emptyAccount]);

  useEffect(() => { load(); }, [load]);

  const simulate = async (sampleId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/me/samples/${sampleId}/simulate`);
      toast.success(ts(res.sample.status));
      await load();
    } catch (e) {
      toast.info(apiError(e.response?.data?.detail, e.message));
    } finally {
      setBusy(false);
    }
  };

  const exportData = async () => {
    const { data: res } = await api.post("/me/export");
    const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "docthair-data-export.json";
    a.click();
    toast.success(t("portal.exported"));
  };

  const nav = [
    { key: "overview", label: t("portal.overview") },
    { key: "tracking", label: t("portal.tracking") },
    { key: "reports", label: t("portal.reports") },
    { key: "evolution", label: t("portal.evolution") },
    { key: "orders", label: t("portal.orders") },
    { key: "data", label: t("portal.data") },
  ];

  if (!data)
    return (
      <AppShell title={t("portal.title")}>
        <p className="flex items-center gap-3 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.openSession")}</p>
      </AppShell>
    );

  const active = data.active_sample;
  const reports = data.samples.filter((s) => s.report);
  const latest = reports[0];

  return (
    <AppShell title={t("portal.title")} nav={nav} active={tab} onNavigate={setTab} testId="account-shell">
      <div className="mb-8 flex flex-col gap-4 border-b border-black/10 pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <Overline>{t("portal.title")}</Overline>
          <h1 className="mt-3 font-display text-4xl font-light leading-none tracking-tighter">{data.user.name}</h1>
          <p className="mt-2 font-mono-dt text-xs text-zinc-400">
            {active ? `${t("portal.sampleid")}: ${active.sample_id}` : t("portal.nosample")} · {data.user.email}
          </p>
        </div>
        <CTAButton to="/order" testId="account-order-btn">{t("cta.order")}</CTAButton>
      </div>

      {tab === "overview" && (
        <div className="grid gap-6 lg:grid-cols-12" data-testid="account-overview">
          <div className="lg:col-span-4">
            <div className="border border-black/10 bg-white p-6">
              <Overline>{t("portal.score")}</Overline>
              {latest ? (
                <>
                  <div className="mt-5 flex justify-center"><ScoreRing value={latest.report.score} label={tb(latest.report.band)} /></div>
                  <p className="mt-5 text-center font-mono-dt text-[11px] text-zinc-400">{latest.analysis_name}</p>
                </>
              ) : (
                <p className="mt-4 text-sm text-zinc-500">{t("portal.scoreSoon")}</p>
              )}
            </div>
            {active && (
              <div className="mt-6 border border-black/10 bg-white p-6">
                <Overline>{t("portal.tracking")}</Overline>
                <p className="mt-3 font-display text-2xl font-light" data-testid="account-status">{ts(active.status)}</p>
                <div className="mt-5">
                  <Timeline steps={active.steps.map((s) => ({ ...s, label: ts(s.key) }))} testId="account-timeline" />
                </div>
                {active.step_index < 4 && (
                  <button data-testid="simulate-btn" onClick={() => simulate(active.sample_id)} disabled={busy}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-black/20 px-5 py-2.5 text-[12px] font-semibold hover:bg-zinc-50 disabled:opacity-60">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />} {t("portal.simulate")}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="lg:col-span-8">
            {!active ? (
              <div className="border border-black/10 bg-white p-10" data-testid="account-empty">
                <h2 className="font-display text-3xl font-light">{t("portal.nosample")}</h2>
                <p className="mt-4 max-w-lg text-sm text-zinc-600">{t("portal.nosample.body")}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <CTAButton to="/order" testId="empty-order-btn">{t("cta.order")}</CTAButton>
                  <CTAButton to="/questionnaire" testId="empty-quiz-btn" variant="ghost">{t("cta.questionnaire")}</CTAButton>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-3">
                  <KPI label={t("portal.analyses")} value={data.samples.length} testId="kpi-samples" />
                  <KPI label={t("portal.reportsCount")} value={reports.length} testId="kpi-reports" />
                  <KPI label={t("portal.evolution")} value={data.evolution.score_delta != null ? `${data.evolution.score_delta > 0 ? "+" : ""}${data.evolution.score_delta} pts` : "—"} testId="kpi-delta" />
                </div>
                <div className="mt-6 border border-black/10 bg-white p-6">
                  <Overline>{t("portal.sampleid")}</Overline>
                  <div className="mt-5 flex flex-col gap-6 sm:flex-row sm:items-center">
                    {active.qr_code && <img src={active.qr_code} alt="Sample QR code" className="h-32 w-32 border border-black/10" data-testid="sample-qr" />}
                    <div className="min-w-0">
                      <p className="font-mono-dt text-lg">{active.sample_id}</p>
                      <p className="mt-1 font-mono-dt text-[11px] text-zinc-400">barcode {active.barcode}</p>
                      <p className="mt-4 overline text-[9px]">{t("portal.returnaddress")}</p>
                      <p className="mt-1 text-sm text-zinc-600">{active.return_address}</p>
                    </div>
                  </div>
                </div>
                {data.notifications.length > 0 && (
                  <div className="mt-6 border border-black/10 bg-white p-6">
                    <Overline>{t("portal.notifications")}</Overline>
                    <div className="mt-4 divide-y divide-black/5">
                      {data.notifications.slice(0, 6).map((n, i) => (
                        <div key={i} className="flex flex-wrap items-baseline justify-between gap-3 py-3" data-testid={`notif-${i}`}>
                          <p className="text-sm">{tn(n.template, n.subject)}</p>
                          <p className="font-mono-dt text-[10px] text-zinc-400">{new Date(n.at).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "tracking" && (
        <div className="grid gap-6 md:grid-cols-2" data-testid="account-tracking">
          {data.samples.map((s) => (
            <div key={s.sample_id} className="border border-black/10 bg-white p-6" data-testid={`track-${s.sample_id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="font-mono-dt text-sm">{s.sample_id}</p>
                <StatusPill>{ts(s.status)}</StatusPill>
              </div>
              <p className="mt-2 text-sm text-zinc-500">{s.analysis_name} · {s.lab_name}</p>
              <div className="mt-5">
                <Timeline steps={s.steps.map((x) => ({ ...x, label: ts(x.key) }))} testId={`timeline-${s.sample_id}`} />
              </div>
              {s.step_index >= 4 && s.status !== "report_available" && (
                <p className="mt-3 flex items-center gap-2 font-mono-dt text-[10px] text-zinc-400"><FlaskConical className="h-3 w-3" /> {t("portal.labtakesover")}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "reports" && (
        <div className="grid gap-8" data-testid="account-reports">
          {reports.length === 0 && <p className="text-sm text-zinc-500">{t("portal.noreport")}</p>}
          {reports.map((s) => (
            <div key={s.sample_id} className="border border-black/10 bg-white p-6 md:p-8" data-testid={`report-${s.sample_id}`}>
              <div className="grid gap-8 md:grid-cols-[200px_1fr]">
                <div>
                  <Overline>{t("portal.score")}</Overline>
                  <div className="mt-4"><ScoreRing value={s.report.score} label={tb(s.report.band)} size={150} /></div>
                  <p className="mt-4 font-mono-dt text-[10px] text-zinc-400">{s.sample_id}<br />{s.analysis_name}<br />{s.report.lab.name}</p>
                </div>
                <div>
                  <Overline>{t("portal.simple")}</Overline>
                  <p className="mt-3 text-base leading-relaxed text-zinc-700">{pick(s.report.interpretation.simple)}</p>
                  <Overline className="mt-6">{t("portal.scientific")}</Overline>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">{pick(s.report.interpretation.scientific)}</p>
                  <p className="mt-3 font-mono-dt text-[10px] text-zinc-400">
                    {t("portal.aiNote").replace("{x}", s.report.interpretation.generated_by)}
                  </p>
                  {s.report.categories?.length ? (
                    <div className="mt-6 h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={s.report.categories.map((c) => ({ subject: t(`cat.${c.category}`), score: c.score }))}>
                          <PolarGrid stroke="#E4E4E7" />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "#71717A" }} />
                          <Radar dataKey="score" stroke="#0A0A0A" fill="#0A0A0A" fillOpacity={0.1} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-8">
                <Overline>{t("portal.params")}</Overline>
                <div className="mt-4">
                  {s.report.metrics.map((m, i) => { const l = tm(m.key); return <MetricRow key={m.key} metric={l ? { ...m, label: l[0], description: l[1] } : m} delay={i * 40} />; })}
                </div>
              </div>
              <div className="mt-8">
                <Overline>{t("portal.recos")}</Overline>
                <div className="mt-4 grid gap-px bg-black/10 sm:grid-cols-2">
                  {s.report.recommendations.map((r) => (
                    <div key={r.id} className="bg-white p-5" data-testid={`reco-${r.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <p className="overline text-[9px]">{r.kind} · {r.commercial ? t("portal.commercial") : t("portal.fromLab")}</p>
                        {r.price ? <p className="font-mono-dt text-sm">€{r.price}</p> : null}
                      </div>
                      <h4 className="mt-3 font-display text-xl font-light">{r.title}</h4>
                      <p className="mt-2 text-sm text-zinc-600">{r.body}</p>
                    </div>
                  ))}
                  {s.report.recommendations.length === 0 && (
                    <p className="bg-white p-5 text-sm text-zinc-500">{t("portal.allOptimal")}</p>
                  )}
                </div>
              </div>
              <p className="mt-6 text-[11px] text-zinc-400">{pick(s.report.disclaimer) || t("legal.disclaimer")}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "evolution" && (
        <div className="grid gap-6" data-testid="account-evolution">
          {data.evolution.points.length < 2 ? (
            <p className="text-sm text-zinc-500">{t("portal.evolution.empty")}</p>
          ) : (
            <>
              <div className="border border-black/10 bg-white p-6">
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <Overline>{t("portal.score")}</Overline>
                    <p className="mt-2 font-display text-3xl font-light">
                      {data.evolution.points[0].score} → {data.evolution.points.at(-1).score}
                    </p>
                  </div>
                  <p className="font-mono-dt text-sm" data-testid="evolution-delta">
                    {data.evolution.score_delta > 0 ? "+" : ""}{data.evolution.score_delta} {t("portal.points")}
                  </p>
                </div>
                <div className="mt-6 h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.evolution.points.map((p) => ({ ...p, date: new Date(p.at).toLocaleDateString() }))}>
                      <CartesianGrid stroke="#EFEFF1" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }} />
                      <Line type="monotone" dataKey="score" stroke="#0A0A0A" strokeWidth={1.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
                {data.evolution.metrics.map((m) => (
                  <div key={m.key} className="bg-white p-5" data-testid={`evo-${m.key}`}>
                    <p className="text-sm font-semibold">{(tm(m.key) || [m.label])[0]}</p>
                    <div className="mt-3 flex items-baseline gap-3 font-mono-dt text-xs text-zinc-500">
                      {m.series.map((s, i) => (
                        <span key={i}>
                          {new Date(s.at).toLocaleDateString(undefined, { month: "short" })}: {s.value}
                        </span>
                      ))}
                    </div>
                    {m.delta != null && (
                      <p className={`mt-3 font-mono-dt text-sm ${m.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {m.delta > 0 ? "+" : ""}{m.delta} {t("portal.points")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-black/10 bg-white p-6" data-testid="account-orders">
          <Overline>{t("portal.orders")}</Overline>
          <div className="mt-4 divide-y divide-black/5">
            {data.orders.map((o) => (
              <div key={o.order_ref} className="flex flex-wrap items-baseline justify-between gap-3 py-3" data-testid={`order-${o.order_ref}`}>
                <div>
                  <p className="font-mono-dt text-sm">{o.order_ref}</p>
                  <p className="text-xs text-zinc-500">{o.analysis_code} · {o.sample_id}</p>
                </div>
                <div className="flex items-center gap-4">
                  <StatusPill>{o.payment_status}</StatusPill>
                  <p className="font-mono-dt text-sm">€{o.amount}</p>
                </div>
              </div>
            ))}
            {data.orders.length === 0 && <p className="py-3 text-sm text-zinc-500">{t("portal.noorder")}</p>}
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className="grid gap-6 md:grid-cols-2" data-testid="account-data">
          <div className="border border-black/10 bg-white p-6">
            <Overline>{t("portal.myQuiz")}</Overline>
            {data.questionnaire ? (
              <div className="mt-4 grid gap-2">
                {Object.entries(data.questionnaire).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-black/5 py-1.5 text-xs">
                    <span className="text-zinc-500">{k}</span><span>{v}</span>
                  </div>
                ))}
              </div>
            ) : <p className="mt-3 text-sm text-zinc-500">{t("portal.quizEmpty")}</p>}
            <Link to="/questionnaire" data-testid="data-quiz-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold">
              {t("cta.questionnaire")} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="border border-black/10 bg-white p-6">
            <Overline>RGPD</Overline>
            <p className="mt-3 text-sm text-zinc-600">
              {t("portal.gdprBody")}
            </p>
            <button onClick={exportData} data-testid="export-btn" className="mt-5 inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 text-[12px] font-semibold hover:bg-zinc-50">
              <Download className="h-3.5 w-3.5" /> {t("portal.export")}
            </button>
            <button
              data-testid="delete-account-btn"
              onClick={async () => {
                if (!window.confirm(t("portal.deleteConfirm"))) return;
                await api.delete("/me");
                toast.success(t("portal.deleted"));
                window.location.href = "/";
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-[12px] font-semibold text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> {t("portal.delete")}
            </button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
